import { ImageReplaceField } from "@/components/ui/report/ImageReplaceField/ImageReplaceField";
import { isReportBannerImage } from "@/lib/report/prepareReportSpecForRender";
import { getTemplateImageSrc } from "@/lib/template-studio/draft/templateStudioDraft";
import { resolveLayoutAssetPreviewUrl } from "@/lib/template-studio/layoutAssetPreviewUrl";
import {
  ingestGraphQLTemplateLayoutAssetFromUrl,
  type TemplateLayoutAsset,
  type TemplateLayoutAssetRepositoryResult,
  uploadGraphQLTemplateLayoutAsset,
} from "@/lib/template-studio/repository/graphqlTemplateLayoutAssetRepository";
import useBriefingCreateStore from "@/store/scheduled-briefings/briefing-create/briefing-create.slice";
import type { TemplateLayoutDocument } from "@/types/template-layout/templateLayout.types";

export interface BriefingImageAssetsSectionProps {
  document: TemplateLayoutDocument;
  /** The template's Image slots in layout order. */
  imageSlotIds: readonly string[];
  /** Read-only briefings show their images without Replace or Reset. */
  isDisabled?: boolean;
}

/** One replace field per template Image slot. The draft changes only when an image is applied. */
export const BriefingImageAssetsSection = ({
  document,
  imageSlotIds,
  isDisabled = false,
}: BriefingImageAssetsSectionProps) => {
  const imageAssets = useBriefingCreateStore((state) => state.imageAssets);
  const setImageAsset = useBriefingCreateStore((state) => state.setImageAsset);
  const clearImageAsset = useBriefingCreateStore(
    (state) => state.clearImageAsset,
  );

  const applyAsset = async (
    slotId: string,
    request: Promise<TemplateLayoutAssetRepositoryResult<TemplateLayoutAsset>>,
  ): Promise<string | null> => {
    const result = await request;
    if (result.success) {
      setImageAsset(slotId, result.data);
      return null;
    }
    // Invalid-asset messages come from the API's image checks; request failures can be raw network errors.
    return result.error.code === "INVALID_ASSET"
      ? result.error.message
      : "The image couldn't be applied. Try again.";
  };

  const bannerSlotIds = imageSlotIds.filter((slotId) =>
    isReportBannerImage(document.spec.elements[slotId]),
  );
  // Banners are Header image, numbered when there are several; other images use their alt text or their position.
  const getLabel = (slotId: string, index: number) => {
    const bannerIndex = bannerSlotIds.indexOf(slotId);
    if (bannerIndex !== -1) {
      return bannerSlotIds.length === 1
        ? "Header image"
        : `Header image ${bannerIndex + 1}`;
    }
    const alt = document.spec.elements[slotId]?.props?.alt;
    return typeof alt === "string" && alt.trim()
      ? alt.trim()
      : `Image ${index + 1}`;
  };

  return (
    <div className="flex flex-col gap-6">
      {imageSlotIds.map((slotId, index) => {
        const label = getLabel(slotId, index);
        const replacementUrl = imageAssets[slotId]?.url;

        return (
          <ImageReplaceField
            key={slotId}
            label={label}
            src={resolveLayoutAssetPreviewUrl(
              replacementUrl || getTemplateImageSrc(document, slotId),
            )}
            alt={label}
            onFileApply={(file) =>
              applyAsset(slotId, uploadGraphQLTemplateLayoutAsset(file))
            }
            onUrlApply={(url) =>
              applyAsset(slotId, ingestGraphQLTemplateLayoutAssetFromUrl(url))
            }
            onReset={
              imageAssets[slotId] ? () => clearImageAsset(slotId) : undefined
            }
            isDisabled={isDisabled}
          />
        );
      })}
    </div>
  );
};
